import type { LucideIcon } from 'lucide-react';
import { Archive, ArchiveRestore, Square } from 'lucide-react';
import { hasBackgroundOnlyWork } from '@/hooks/agents/agent-activity-utils';
import { isAgentActive, isWorkingActivity } from '@/lib/status-colors';
import type { Agent, AgentActivity } from '@/lib/types';

// Typed as `LucideIcon` (not a generic `ComponentType`) so these icons drop
// straight into surfaces that demand it — e.g. the sidebar row's
// `MenuActionItem`, whose `icon` field is a `LucideIcon`.
type AgentActionIcon = LucideIcon;

export type PrimaryAgentActionKind = 'stop' | 'archive' | 'unarchive';

export interface PrimaryAgentAction {
  kind: PrimaryAgentActionKind;
  /** User-facing control label (also the accessible name). */
  label: string;
  icon: AgentActionIcon;
}

/**
 * The single lifecycle control an agent surfaces as its primary action, shared
 * by the stream header, the detail panel, and the sidebar row so the three
 * never drift. It tracks the run's lifecycle through one slot:
 *
 * - **Stop** while a turn is in flight — i.e. the agent is actually working.
 *   Stop interrupts the current turn (leaving the sandbox up); it is *not* the
 *   sandbox teardown. Tearing the sandbox down happens only via Delete.
 * - **Archive** once it stops working (idle, awaiting input, interrupted, or
 *   terminated). A live-but-idle agent has no turn to interrupt, so Stop is
 *   hidden for it — including one that reads as `working` only because the
 *   broker still runs background tasks after its turn ended
 *   ({@link hasBackgroundOnlyWork}): interrupting it would fire at nothing and
 *   pause the run while the tasks keep going.
 * - **Unarchive** once archived.
 *
 * `activity` is the derived working/idle signal. When omitted (callers that do
 * not track per-agent activity) the coarser status-based liveness check
 * ({@link isAgentActive}) stands in, preserving the pre-activity behaviour.
 */
export function primaryAgentAction(
  agent: Pick<Agent, 'status' | 'archived'> &
    Partial<Pick<Agent, 'turnState' | 'hasBackgroundWork'>>,
  activity?: AgentActivity,
): PrimaryAgentAction {
  const canStop = activity
    ? isWorkingActivity(activity) && !hasBackgroundOnlyWork(withTurnFields(agent))
    : isAgentActive(agent);
  if (canStop) return { kind: 'stop', label: 'Stop', icon: Square };
  if (agent.archived) return { kind: 'unarchive', label: 'Unarchive', icon: ArchiveRestore };
  return { kind: 'archive', label: 'Archive', icon: Archive };
}

/** Callers without the durable turn fields carry no background evidence. */
function withTurnFields(
  agent: Pick<Agent, 'status'> & Partial<Pick<Agent, 'turnState' | 'hasBackgroundWork'>>,
): Pick<Agent, 'status' | 'turnState' | 'hasBackgroundWork'> {
  return {
    status: agent.status,
    turnState: agent.turnState ?? null,
    hasBackgroundWork: agent.hasBackgroundWork ?? false,
  };
}
